// Components==============
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import showFlashExercise from 'actions/flashExercise/show';
import FlashGraph from 'components/Exercises/FlashExercise/components/FlashGraph/FlashGraph';
import FlashExercise from 'components/Exercises/FlashExercise/FlashExercise';
import BackButton from 'components/Layout/BackButton/BackButton';
import Background from 'components/Layout/Background/Background';
import Menu from 'components/Layout/Menu/Menu';
import NavigationBox, {
    Displaymode,
} from 'components/Layout/NavigationBox/NavigationBox';
import React, { createContext, useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import TimeBar from './components/TimeBar';
// =========================

export type BarStatus = 'loading' | 'loaded' | 'initial';

type FlashContextType = {
    isFinished: boolean;
    setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
    exercise?: FlashExerciseEntity & UserFlashExercise;
    barStatus: BarStatus;
    setBarStatus: React.Dispatch<React.SetStateAction<BarStatus>>;
    current: number;
    setCurrent: React.Dispatch<React.SetStateAction<number>>;
    background: Background;
};

export const FlashContext = createContext({} as FlashContextType);

export default function Flash() {
    const [top, setTop] = useState('2em');
    const [displayMode, setDisplayMode] = useState<Displaymode>('centered-y');
    const [isFinished, setIsFinished] = useState(false);
    const [current, setCurrent] = useState(-1);

    const [barStatus, setBarStatus] = useState<BarStatus>('initial');

    const dispatch = useDispatch();
    const { treatmentId, exerciseId } = useParams() as {
        treatmentId: string;
        exerciseId: string;
    };

    useEffect(() => {
        dispatch(showFlashExercise(exerciseId));
    }, []);

    useEffect(() => {
        if (isFinished) {
            setTop('50%');
            setDisplayMode('centered');
            return;
        }
        setTop('2em');
        setDisplayMode('centered-y');
    }, [isFinished]);

    const exercise = useSelector(
        ({ flashExercise }: { flashExercise: FlashExercise }) =>
            flashExercise.entities.byId[exerciseId]
    );

    const background = useSelector(
        (state: ReduxStore) =>
            state?.treatment?.entities?.byId[treatmentId]?.world || 'city'
    );

    const user = useSelector(
        (state: ReduxStore) => state?.authentication?.user
    );

    const userFlashInfo = user?.flash_exercises?.find(
        (e) => e.id === exercise?.id
    );

    const exerciseWithUserFlashInfo = {
        ...exercise,
        ...userFlashInfo,
    } as FlashExerciseEntity & UserFlashExercise;

    const isAlreadyFinished =
        typeof user?.exercise_status?.find(
            (e) =>
                e.exercise_id === exercise?.id &&
                e.exercise_type.includes('FlashExercise')
        ) !== 'undefined';

    const hasAchievedGoal =
        current + 1 >= exerciseWithUserFlashInfo?.pivot?.needs_correct;

    useEffect(() => {
        if (isFinished && !isAlreadyFinished && hasAchievedGoal)
            dispatch(
                updateExerciseStatus({
                    exercise_id: exercise?.id,
                    exercise_type: 'flash',
                })
            );
    }, [isFinished]);

    return (
        <FlashContext.Provider
            value={{
                isFinished,
                setIsFinished,
                exercise: exerciseWithUserFlashInfo,
                barStatus,
                setBarStatus,
                current,
                setCurrent,
                background,
            }}
        >
            <Background background={background}>
                <BackButton />
                <NavigationBox
                    displayMode={displayMode}
                    audio="/audio/exercise_explanation/flash.mp3"
                    subtitle={`${
                        !isFinished
                            ? 'Je gaat nu woorden flitsen. Lees zo goed en zo snel mogelijk de woorden hardop.'
                            : `Je bent klaar met flitsen. Je hebt ${
                                  current + 1
                              } woorden gelezen.`
                    }`}
                    top={top}
                    ProgressBar={
                        <>
                            {!isFinished && (
                                <TimeBar hide={!user?.show_flash_timer} />
                            )}
                        </>
                    }
                    finishedButtons={isFinished && 'flash-exercise'}
                >
                    {isFinished ? <FlashGraph /> : undefined}
                </NavigationBox>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <Confetti active={isFinished && hasAchievedGoal} />
                </div>
                <Menu />
                <FlashExercise />
            </Background>
        </FlashContext.Provider>
    );
}
